import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import Overlay from 'common/components/core/Overlay'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import * as actions from 'client/actions/actionsManagement'
import * as entityActions from 'client/actions/entityActions'
import useManagement from 'client/hooks/useManagement'
import { createPage } from 'client/pages'
import { isSettingsOverlayVisible } from 'client/reducers/managementReducer'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { useIsShowAdditionalAction } from 'client/hooks/use-is-show-additional-action'
import {
  useOverlayActionsContext,
  useOverlayVisibilityContext,
} from './settings-overlay-context'
import { useUpdateEntityStyle } from './hooks/use-update-entity'
import { useRemoveEntityStyle } from './hooks/use-remove-entity-style'
import { useRemoveEntityMobileStyle } from './hooks/use-remove-entity-mobile-style'
import { isLockedDescendant } from './utils/is-locked-descendant'
import OldEntitySettings from 'client/components/core/Sidebar/components/Settings/OldEntitySettings'
import EntityInterface, {
  BlockEntityInterface,
  EntityUnionType,
} from 'common/types/entities/EntityInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { AdditionalProps } from 'client/pages/createCommonEntityElement'
import { BackButton } from 'src/editor/shared/components/back-button'
import {
  ButtonSizeEnum,
  SecondaryButton,
} from 'src/editor/shared/ui-lib/buttons'
import { ChevronLeftIcon } from 'src/editor/shared/icons/chevron-left-icon'

interface EntitySettingsProps {
  entity: (OldEntityInterface | EntityUnionType) & BlockEntityInterface
}

export const EntitySettings = ({ entity }: EntitySettingsProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isColorPickerOverlay = useManagement(isSettingsOverlayVisible)
  const isOverlayVisible = useOverlayVisibilityContext()
  const { close, toggle } = useOverlayActionsContext()
  const updateEntityStyle = useUpdateEntityStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const removeEntityStyle = useRemoveEntityStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const removeEntityMobileStyle = useRemoveEntityMobileStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const moveDown = useActions(entityActions.moveDown)
  const moveUp = useActions(entityActions.moveUp)

  function handleMoveUp(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.stopPropagation()
    moveUp(entity)
  }

  function handleMoveDown(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.stopPropagation()
    moveDown(entity)
  }

  const [showCreateBlockDialog, togglePickerOverlay, toggleSettings] =
    useActions([
      actions.showCreateBlockDialog,
      actions.toggleSettingsOverlay,
      actions.toggleSettings,
    ])
  const copy = useActions(entityActions.copy)
  const remove = useActions(entityActions.remove)
  const pageType = usePage(pageSelectors.getPageType)
  const parentEntity = usePage(page =>
    pageSelectors.getEntityById(page, entity.parentId || null),
  )
  const entityParents = usePage(page =>
    pageSelectors.getEntityParents(page.entities, entity),
  )

  const isShowAdditionalAction = useIsShowAdditionalAction({
    entityParents,
    entity,
  })

  const [, , createEntitySettings] = createPage(pageType)
  const isMasterBlock = Boolean(entity.masterBlockId)
  const isLocked =
    parentEntity &&
    isLockedDescendant(entity.type, parentEntity.type as EntityTypeEnum)
  const isNotPopupEntity = entity.type !== EntityTypeEnum.Popup
  const isSaveAllowed = isNotPopupEntity && !isMasterBlock
  const isCopyAllowed = isNotPopupEntity && !entity.isMasterBlockRoot
  const isRemoveAllowed = isNotPopupEntity

  const safeRemove = () =>
    confirm(
      t(
        isMasterBlock
          ? 'components.core.base_entity.action_box.safe_remove_master'
          : 'components.core.base_entity.action_box.safe_remove',
      ),
    ) && remove(entity)

  const hide = () => {
    close()
    togglePickerOverlay(false)
  }

  return (
    <>
      <div className="mb-8 flex items-center justify-between font-medium">
        <SecondaryButton
          size={ButtonSizeEnum.Small}
          iconBefore={<ChevronLeftIcon />}
          onClick={() => toggleSettings(null)}
        >
          {t('entity_settings.back_to_page')}
        </SecondaryButton>

        <div className="flex items-center gap-2.5">
          {isNotPopupEntity && (
            <button
              type="button"
              className="cursor-pointer text-L"
              onClick={handleMoveUp}
            >
              <i className="fas fa-arrow-up text-gray-700" />
            </button>
          )}
          {isNotPopupEntity && (
            <button
              type="button"
              className="cursor-pointer text-L"
              onClick={handleMoveDown}
            >
              <i className="fas fa-arrow-down text-gray-700" />
            </button>
          )}
          {!isLocked && isShowAdditionalAction && (
            <>
              {isCopyAllowed && (
                <button
                  type="button"
                  title={t('components.core.actions.copy')}
                  className="cursor-pointer text-L"
                  onClick={() => copy(entity)}
                >
                  <i className="far fa-copy text-gray-700" />
                </button>
              )}
              {isSaveAllowed && (
                <button
                  type="button"
                  title={t('components.core.dialog.save_block.header')}
                  className="cursor-pointer text-L"
                  onClick={() => showCreateBlockDialog(entity.id)}
                >
                  <i className="far fa-save text-gray-700" />
                </button>
              )}
              {isRemoveAllowed && (
                <button
                  type="button"
                  title={t('components.core.actions.remove')}
                  className="cursor-pointer text-L"
                  onClick={safeRemove}
                >
                  <i className="far fa-trash-alt text-gray-700" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mt-3 mb-9 flex items-center gap-2">
        <span className="text-heading-4">
          {t(`entity_settings.${entity.type}.header`.toLowerCase())}
        </span>
        {isMasterBlock && (
          <i
            className="fa-light fa-bolt-lightning text-subheading-2"
            title={t('entity_settings.master_block_warning')}
          />
        )}
      </div>
      <div className="h-full">
        {entity.type in EntityTypeEnum ||
        entity.type === EntityTypeEnum.Carousel ||
        entity.type === EntityTypeEnum.CarouselItem ? (
          createEntitySettings(
            entity as EntityInterface & { children: React.ReactNode },
            {} as AdditionalProps,
          )
        ) : (
          <OldEntitySettings
            entity={entity}
            isOverlayActive={isOverlayVisible}
            toggleSettingsOverlay={toggle}
            updateEntityStyle={updateEntityStyle}
            removeEntityStyle={removeEntityStyle}
            removeEntityMobileStyle={removeEntityMobileStyle}
          />
        )}
        {(isOverlayVisible || isColorPickerOverlay) && (
          <Overlay hide={hide} zIndex={30} />
        )}
      </div>
    </>
  )
}
